import { useEffect } from 'react';
import { createEmbeddedMacroListener } from '@atlassian/bridge-core/v2';
import { isCustomUI } from '../../../utils/isCustomUI';
import type { GQLExtension } from '../../../web-client/graphql/types';
import type { BridgeContext } from '../../../bridge/types';
import type { TAnyBridgeMethod } from '../../../bridge/methods/types';
import { createBridgeFeatures } from '../../../bridge/createBridgeFeatures';
import { consolidateCustomBridge } from '../../../bridge/utils';
import { withEmbeddedMacroContext } from '../../../bridge/middleware/withEmbeddedMacroContext';

type UseEmbeddedMacroConnectionInput = {
	/** v2 context-aware custom bridge methods. */
	customBridge?: Record<string, TAnyBridgeMethod>;
	extension: GQLExtension;
	getBridgeContext: () => BridgeContext;
	/** v1 closure-based custom bridge methods (context-unaware). */
	legacyCustomBridgeMethods?: Record<string, (data: unknown) => Promise<unknown>>;
};

export const useEmbeddedMacroConnection = ({
	extension,
	getBridgeContext,
	legacyCustomBridgeMethods,
	customBridge,
}: UseEmbeddedMacroConnectionInput): void => {
	const isCustomUIBodiedMacro = isCustomUI(extension) && extension.properties?.layout === 'bodied';

	useEffect(() => {
		if (!isCustomUIBodiedMacro) {
			return;
		}

		// withEmbeddedMacroContext is injected as the outermost middleware so it:
		//  - unwraps the { embeddedMacroContext, payload } envelope before withMetrics sees it
		//  - provides the correctly merged context (embedded macro identity over host context)
		//    to withMetrics and all inner methods — no mutable refs or manual BridgeFeatures
		//    reconstruction needed.
		const bridgeFeatures = createBridgeFeatures(
			getBridgeContext,
			(baseBridgeMethods) =>
				consolidateCustomBridge(baseBridgeMethods, legacyCustomBridgeMethods, customBridge),
			(defaults) => [withEmbeddedMacroContext, ...defaults],
		);

		const connection = createEmbeddedMacroListener(bridgeFeatures);
		return () => {
			connection?.close();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- getBridgeContext is stable; legacyCustomBridgeMethods and customBridge are intentionally captured at mount only
	}, [isCustomUIBodiedMacro]);
};
